import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { Badge } from "../../../../shadcn/ui/badge";
import { Button } from "../../../../shadcn/ui/button";
import { Users, ChevronLeft, ChevronRight } from "lucide-react";

const SpaceCalendarView = ({ space, bookingData, timeSlots, days, onBookSlot }) => {
  const getStatusColor = status => {
    switch (status) {
      case "✓":
        return "bg-green-200 text-green-800 hover:bg-green-300 cursor-pointer";
      case "B":
        return "bg-orange-200 text-orange-800";
      case "O":
        return "bg-pink-200 text-pink-800";
      case "M":
        return "bg-gray-200 text-gray-800";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const getStatusText = status => {
    switch (status) {
      case "✓":
        return "Available";
      case "B":
        return "Booked";
      case "O":
        return "Occupied";
      case "M":
        return "Maintenance";
      default:
        return "Unknown";
    }
  };

  const handleSlotClick = (time, dayIndex, status) => {
    if (status === "✓" && onBookSlot) {
      onBookSlot(time, dayIndex, space.id);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">{space.name}</CardTitle>
            <div className="flex items-center mt-1 space-x-4 text-sm text-gray-600">
              <span>{space.location}</span>
              <span className="flex items-center">
                <Users className="mr-1 w-4 h-4" />
                {space.capacity} people
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant="outline">{space.type}</Badge>
            <Badge className={space.status === "available" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
              {space.status === "available" ? "Available" : space.status}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-medium">Week of 30/06/2025 - 06/07/2025</span>
            <Button variant="ghost" size="sm">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Legend */}
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-green-200 rounded"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-orange-200 rounded"></div>
              <span>Booked</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-pink-200 rounded"></div>
              <span>Occupied</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-gray-200 rounded"></div>
              <span>Maintenance</span>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="p-2 w-16 text-left"></th>
                {days.map(day => (
                  <th key={day.name} className="text-center p-2 min-w-[100px]">
                    <div className="text-sm font-medium">{day.name}</div>
                    <div className="text-xs text-gray-500">{day.date}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map(time => (
                <tr key={time} className="border-t">
                  <td className="p-2 text-sm font-medium text-gray-600">{time}</td>
                  {bookingData[time]?.map((status, dayIndex) => (
                    <td key={dayIndex} className="p-1">
                      <div
                        className={`flex justify-center items-center w-full h-8 text-xs font-medium text-center rounded transition-colors ${getStatusColor(status)}`}
                        onClick={() => handleSlotClick(time, dayIndex, status)}
                        title={`${time} - ${getStatusText(status)}`}
                      >
                        {status}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Booking Instructions */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>How to book:</strong> Click on any green (✓) slot to book that time. Orange slots are already
            booked, pink slots are currently occupied, and gray slots are under maintenance.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SpaceCalendarView;
